import { I18nService } from '../i18n/I18nService';
import { I18N_KEYS } from '../i18n/keys';
import { GameStateMachine } from '../core/GameStateMachine';
import { ManifestService } from '../services/ManifestService';
import { ToastService } from '../services/ToastService';
import { AccessibilityManager } from '../accessibility/AccessibilityManager';
import { FocusTrap } from '../accessibility/FocusTrap';
import { HeaderBar } from './components/HeaderBar';
import { CurrentNumberDisplay } from './components/CurrentNumberDisplay';
import { ControlBar } from './components/ControlBar';
import { NumberGrid } from './components/NumberGrid';
import { StatusBar } from './components/StatusBar';
import { SettingsModal } from './components/SettingsModal';
import { ConfirmModal } from './components/ConfirmModal';
import { CompletionModal } from './components/CompletionModal';
import { HistoryModal } from './components/HistoryModal';
import { KeyboardHelpModal } from './components/KeyboardHelpModal';
import { GuidelineModal } from './components/GuidelineModal';
import { DonateModal } from './components/DonateModal';
import { TermsModal } from './components/TermsModal';
import { EmergencyBanner } from './components/EmergencyBanner';
import { MultiTabBanner } from './components/MultiTabBanner';
import { GameSettings, RuntimeState, GamePhase, UILocale } from '../types';
import { SKIP_AUDIO_THRESHOLD_SECONDS } from '../constants/skipAudioConfig';
import { formatElapsedTime, getElapsedMs } from '../utils/time';

export class UIController {
  private i18n: I18nService;
  private gsm: GameStateMachine;
  private manifest: ManifestService;
  private toast: ToastService;
  private a11y: AccessibilityManager;

  private header: HeaderBar;
  private display: CurrentNumberDisplay;
  private controlBar: ControlBar;
  private grid: NumberGrid;
  private statusBar: StatusBar;
  private settingsModal: SettingsModal;
  private confirmModal: ConfirmModal;
  private completionModal: CompletionModal;
  private historyModal: HistoryModal;
  private keyboardHelpModal: KeyboardHelpModal;
  private guidelineModal: GuidelineModal;
  private donateModal: DonateModal;
  private termsModal: TermsModal;
  private emergencyBanner: EmergencyBanner;
  private multiTabBanner: MultiTabBanner;

  private focusTrap: FocusTrap;
  private statusTimerId: ReturnType<typeof setInterval> | null = null;
  private skipShown: boolean = false;
  private eventsBound: boolean = false;
  private previewAudio: HTMLAudioElement | null = null;

  constructor(
    i18n: I18nService,
    gsm: GameStateMachine,
    manifest: ManifestService,
    toast: ToastService,
    a11y: AccessibilityManager
  ) {
    this.i18n = i18n;
    this.gsm = gsm;
    this.manifest = manifest;
    this.toast = toast;
    this.a11y = a11y;

    this.focusTrap = new FocusTrap();
    this.header = new HeaderBar(i18n);
    this.display = new CurrentNumberDisplay(i18n);
    this.controlBar = new ControlBar(i18n);
    this.grid = new NumberGrid(i18n, a11y);
    this.statusBar = new StatusBar(i18n);
    this.settingsModal = new SettingsModal(i18n, this.focusTrap);
    this.confirmModal = new ConfirmModal(i18n, this.focusTrap);
    this.completionModal = new CompletionModal(i18n, this.focusTrap);
    this.historyModal = new HistoryModal(i18n, this.focusTrap);
    this.keyboardHelpModal = new KeyboardHelpModal(i18n, this.focusTrap);
    this.guidelineModal = new GuidelineModal(i18n, this.focusTrap);
    this.donateModal = new DonateModal(i18n, this.focusTrap);
    this.termsModal = new TermsModal(i18n, this.focusTrap);
    this.emergencyBanner = new EmergencyBanner(i18n);
    this.multiTabBanner = new MultiTabBanner(i18n);
  }

  render(): void {
    const state = this.gsm.getState();

    this.header.render();
    this.display.render();
    this.controlBar.render(state.gamePhase, state.settings.autoPlayEnabled);
    this.grid.render(state.settings.min, state.settings.max, state.session.calledNumbers, state.session.currentNumber);
    this.statusBar.render(this.gsm.getCalledCount(), this.gsm.getTotalCount(), this.gsm.getElapsedTime());

    this.settingsModal.render();
    this.confirmModal.render();
    this.completionModal.render();
    this.historyModal.render();
    this.keyboardHelpModal.render();
    this.guidelineModal.render();
    this.donateModal.render();
    this.termsModal.render();
    this.multiTabBanner.initialize();

    // Show initial state
    if (state.gamePhase === 'IDLE') {
      this.display.showReady();
      this.controlBar.updateNextButtonLabel(true);
      this.controlBar.setNextButtonEnabled(true);
    } else if (state.gamePhase === 'COMPLETE') {
      this.controlBar.setNextButtonEnabled(false);
    } else if (state.gamePhase === 'PLAYING' && state.session.currentNumber) {
      this.display.showNumber(state.session.currentNumber);
    }

    if (this.manifest.isEmergencyMode()) {
      this.emergencyBanner.show();
    }

    this.bindEvents();
    this.startStatusTimer();
  }

  bindEvents(): void {
    // Prevent duplicate event bindings
    if (this.eventsBound) {
      return;
    }
    this.eventsBound = true;

    const state = this.gsm.getState();

    // Header events
    this.header.onSettingsClick = () => this.openSettings();
    this.header.onGuidelineClick = () => this.openGuideline();
    this.header.onDonateClick = () => this.openDonate();

    this.statusBar.onTermsClick = () => this.openTerms();

    // Display events
    this.display.onSkipAudio = () => this.gsm.forceSkipAudio();

    // Control bar events
    this.controlBar.onNextNumber = () => this.handleNextNumber();
    this.controlBar.onToggleAutoPlay = () => this.gsm.toggleAutoPlay();
    this.controlBar.onReset = () => this.handleReset();

    // Settings modal events
    this.settingsModal.onApply = (settings) => this.handleSettingsApply(settings);
    this.settingsModal.onClose = () => {
      this.gsm.resumeAutoPlay(true);
      this.stopPreview();
    };
    this.settingsModal.onPreview = (lang: string, style: string) => {
      console.log('[UIController] onPreview callback invoked with lang:', lang, 'style:', style);
      this.handlePreview(lang, style);
    };

    // Confirm modal events
    this.confirmModal.onConfirm = () => {
      this.gsm.reset();
      this.refreshUI();
    };
    this.confirmModal.onCancel = () => {
      this.gsm.resumeAutoPlay(true);
    };

    // Completion modal events
    this.completionModal.onViewHistory = () => {
      const s = this.gsm.getState();
      this.historyModal.open(s.session.calledNumbers, s.session.startTime);
    };
    this.completionModal.onNewGame = () => {
      this.gsm.reset();
      this.refreshUI();
    };

    // Emergency banner
    this.emergencyBanner.onRetry = async () => {
      const success = await this.manifest.retryLoad();
      if (success) {
        this.emergencyBanner.hide();
        this.gsm.setEmergencyMode(false);
        this.toast.show(this.i18n.t(I18N_KEYS.TOAST_CONNECTION_RESTORED), 'success');
      }
    };

    // Game state machine events
    this.gsm.on('numberCalled', (number: unknown) => {
      const num = number as number;
      this.display.showNumber(num);
      this.grid.highlightCell(num);
      this.controlBar.setNextButtonEnabled(true);
      this.a11y.announceNumber(num);
      this.skipShown = false;
      this.updateStatus();
    });

    this.gsm.on('audioStarted', () => {
      this.display.showPlaying();
      this.controlBar.setNextButtonEnabled(false);
      this.controlBar.hideCountdown();
      this.skipShown = false;
    });

    this.gsm.on('audioError', (payload: unknown) => {
      const p = payload as { number: number; error: Error };
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_AUDIO_UNAVAILABLE, { number: p.number }), 'warning');
      this.grid.markSilent(p.number);
    });

    this.gsm.on('gameComplete', () => {
      const s = this.gsm.getState();
      const elapsed = this.gsm.getElapsedTime();
      const styleName = this.manifest.getStyles(s.settings.language)
        .find(st => st.key === s.settings.style)?.name || s.settings.style;
      this.a11y.announceGameComplete(this.gsm.getCalledCount(), elapsed);
      this.completionModal.open(this.gsm.getCalledCount(), elapsed, styleName);
      this.controlBar.setNextButtonEnabled(false);
    });

    this.gsm.on('autoPlayToggled', (enabled: unknown) => {
      this.controlBar.updateAutoPlayState(enabled as boolean);
    });

    this.gsm.on('autoPlayPaused', () => {
      this.controlBar.hideCountdown();
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_AUTO_PLAY_PAUSED), 'info');
    });

    this.gsm.on('autoPlayResumed', () => {
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_AUTO_PLAY_RESUMED), 'info');
    });

    this.gsm.on('timerTick', (remaining: unknown) => {
      this.controlBar.showCountdown(remaining as number);
    });

    this.gsm.on('phaseChanged', (phase: unknown) => {
      const p = phase as GamePhase;
      console.log('[UIController] Phase changed to:', p);
      this.controlBar.updateNextButtonLabel(p === 'IDLE');
      if (p === 'IDLE') {
        this.display.showReady();
        this.controlBar.setNextButtonEnabled(true); // Ensure button is enabled in IDLE
      } else if (p === 'COMPLETE') {
        this.controlBar.setNextButtonEnabled(false); // Ensure button is disabled in COMPLETE
      }
    });

    this.gsm.on('settingsChanged', () => {
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_SETTINGS_SAVED), 'success');
    });

    this.gsm.on('emergencyMode', () => {
      this.emergencyBanner.show();
    });

    // I18n locale change
    this.i18n.onLocaleChanged = (locale: UILocale) => {
      this.updateDocumentTitle(locale);
      this.refreshAllLabels();
    };

    // Visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.gsm.pauseAutoPlay();
      } else {
        this.gsm.resumeAutoPlay();
      }
    });

    // Audio progress tracking
    this.gsm.on('audioStarted', (_num: unknown, elapsed?: unknown) => {
      if (typeof elapsed === 'number' && elapsed >= SKIP_AUDIO_THRESHOLD_SECONDS && !this.skipShown) {
        this.display.showSkipButton();
        this.skipShown = true;
      }
    });
  }

  handleNextNumber(): void {
    console.log('[UIController] handleNextNumber called');
    const state = this.gsm.getState();
    console.log('[UIController] Current state - phase:', state.gamePhase, 'audio playing:', state.isAudioPlaying, 'queue length:', state.session.shuffledQueue.length);
    
    if (state.gamePhase === 'COMPLETE') {
      console.log('[UIController] Game is complete, resetting and starting new game');
      this.gsm.reset();
      this.gsm.startGame();
      this.gsm.nextNumber();
      return;
    }
    
    if (state.gamePhase === 'IDLE') {
      console.log('[UIController] Starting game and calling nextNumber');
      this.gsm.startGame();
      this.gsm.nextNumber();
      return;
    }
    
    if (state.isAudioPlaying) {
      console.log('[UIController] Audio playing, showing wait toast');
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_PLEASE_WAIT), 'info');
      return;
    }
    
    console.log('[UIController] Calling nextNumber');
    this.gsm.nextNumber();
  }

  handleReset(): void {
    const state = this.gsm.getState();
    if (state.gamePhase === 'IDLE' && state.session.calledNumbers.length === 0) return;
    this.gsm.pauseAutoPlay(true);
    this.confirmModal.open(this.gsm.getCalledCount(), this.gsm.getElapsedTime());
  }

  openSettings(): void {
    console.log('[UIController] openSettings called');
    this.gsm.pauseAutoPlay(true);
    const state = this.gsm.getState();
    const languages = this.manifest.getLanguages();
    const styles = this.manifest.getStyles(state.settings.language);
    const maxFiles = this.manifest.getFileCount(state.settings.language, state.settings.style);
    
    // Set callbacks for dynamic style updates
    this.settingsModal.onGetStylesForLanguage = (lang: string) => this.manifest.getStyles(lang);
    this.settingsModal.onGetFileCount = (lang: string, style: string) => this.manifest.getFileCount(lang, style);
    
    console.log('[UIController] onPreview callback set:', !!this.settingsModal.onPreview);
    console.log('[UIController] Opening settings modal with languages:', languages.length, 'styles:', styles.length);
    
    this.settingsModal.open(state.settings, languages, styles, state.gamePhase === 'PLAYING', maxFiles);
  }

  openKeyboardHelp(): void {
    this.keyboardHelpModal.open();
  }

  openGuideline(): void {
    this.guidelineModal.open();
  }

  openDonate(): void {
    this.donateModal.open();
  }

  openTerms(): void {
    this.termsModal.open();
  }

  closeAllModals(): void {
    if (this.settingsModal.isOpen()) this.settingsModal.close();
    if (this.confirmModal.isOpen()) this.confirmModal.close();
    if (this.completionModal.isOpen()) this.completionModal.close();
    if (this.historyModal.isOpen()) this.historyModal.close();
    if (this.keyboardHelpModal.isOpen()) this.keyboardHelpModal.close();
    if (this.guidelineModal.isOpen()) this.guidelineModal.close();
    if (this.donateModal.isOpen()) this.donateModal.close();
    if (this.termsModal.isOpen()) this.termsModal.close();
  }

  isAnyModalOpen(): boolean {
    return (
      this.settingsModal.isOpen() ||
      this.confirmModal.isOpen() ||
      this.completionModal.isOpen() ||
      this.historyModal.isOpen() ||
      this.keyboardHelpModal.isOpen() ||
      this.guidelineModal.isOpen() ||
      this.donateModal.isOpen() ||
      this.termsModal.isOpen()
    );
  }

  private handleSettingsApply(settings: GameSettings): void {
    const currentState = this.gsm.getState();
    // Preserve autoPlayEnabled from current state
    settings.autoPlayEnabled = currentState.settings.autoPlayEnabled;

    // Handle UI locale change
    if (settings.uiLocale !== this.i18n.getLocale()) {
      this.i18n.setLocale(settings.uiLocale);
    }

    this.gsm.applySettings(settings);
    
    // Refresh UI to reflect settings changes
    const newState = this.gsm.getState();
    this.header.render();
    
    // Update grid if range changed
    if (newState.settings.min !== currentState.settings.min || 
        newState.settings.max !== currentState.settings.max) {
      this.grid.render(newState.settings.min, newState.settings.max, 
                      newState.session.calledNumbers, newState.session.currentNumber);
    }
  }

  private refreshUI(): void {
    const state = this.gsm.getState();
    this.display.showReady();
    this.controlBar.updateNextButtonLabel(state.gamePhase === 'IDLE');
    this.controlBar.setNextButtonEnabled(true);
    this.controlBar.updateAutoPlayState(state.settings.autoPlayEnabled);
    this.controlBar.hideCountdown();
    this.grid.render(state.settings.min, state.settings.max, state.session.calledNumbers, state.session.currentNumber);
    this.updateStatus();
  }

  private refreshAllLabels(): void {
    this.header.updateLabels();
    this.display.updateLabels();
    const state = this.gsm.getState();
    // If showing ready state, update it
    if (state.gamePhase === 'IDLE' && state.session.calledNumbers.length === 0) {
      this.display.showReady();
    }
    this.controlBar.updateLabels(state.gamePhase === 'IDLE');
    this.statusBar.updateLabels(this.gsm.getCalledCount(), this.gsm.getTotalCount(), this.gsm.getElapsedTime());
  }

  private updateStatus(): void {
    this.statusBar.update(this.gsm.getCalledCount(), this.gsm.getTotalCount(), this.gsm.getElapsedTime());
  }

  private startStatusTimer(): void {
    this.statusTimerId = setInterval(() => {
      const state = this.gsm.getState();
      if (state.gamePhase === 'PLAYING') {
        this.updateStatus();
      }
    }, 1000);
  }

  private handlePreview(lang: string, style: string): void {
    console.log('[UIController] handlePreview called with lang:', lang, 'style:', style);
    
    // Stop any existing preview
    this.stopPreview();

    // Get preview URL from manifest
    const previewUrl = this.manifest.getPreviewUrl(lang, style);
    console.log('[UIController] Preview URL from manifest:', previewUrl);
    
    if (!previewUrl) {
      console.log('[UIController] No preview URL, showing warning toast');
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_PREVIEW_NOT_AVAILABLE), 'warning');
      return;
    }

    // Resolve preview URL (handle relative/absolute paths)
    let resolvedUrl: string;
    const baseUrl = this.manifest.getAudioBaseUrl();
    console.log('[UIController] Base URL:', baseUrl);
    
    if (/^https?:\/\//.test(previewUrl)) {
      // Absolute URL
      resolvedUrl = previewUrl;
      console.log('[UIController] Using absolute URL:', resolvedUrl);
    } else if (previewUrl.startsWith('/')) {
      // Relative to origin
      resolvedUrl = previewUrl;
      console.log('[UIController] Using relative path:', resolvedUrl);
    } else {
      // Relative to base URL
      resolvedUrl = `${baseUrl}/${lang}/${style}/${previewUrl}`;
      console.log('[UIController] Resolved relative URL:', resolvedUrl);
    }

    // Create and play preview audio
    console.log('[UIController] Creating Audio element with URL:', resolvedUrl);
    this.previewAudio = new Audio(resolvedUrl);
    this.previewAudio.volume = 1.0;
    
    this.previewAudio.addEventListener('ended', () => {
      console.log('[UIController] Preview audio ended');
      this.settingsModal.setPreviewButtonState(false);
      this.previewAudio = null;
    });

    this.previewAudio.addEventListener('error', (e) => {
      console.error('[UIController] Preview audio error:', e);
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_PREVIEW_FAILED), 'error');
      this.settingsModal.setPreviewButtonState(false);
      this.previewAudio = null;
    });

    console.log('[UIController] Attempting to play preview audio');
    
    // Update button state to show playing
    this.settingsModal.setPreviewButtonState(true);
    
    // Show toast that preview is starting
    this.toast.show(this.i18n.t(I18N_KEYS.TOAST_PREVIEW_PLAYING), 'info');
    
    this.previewAudio.play().then(() => {
      console.log('[UIController] Preview audio started playing');
    }).catch((error) => {
      console.error('[UIController] Preview audio play failed:', error);
      this.toast.show(this.i18n.t(I18N_KEYS.TOAST_PREVIEW_FAILED), 'error');
      this.previewAudio = null;
      this.settingsModal.setPreviewButtonState(false);
    });
  }

  private stopPreview(): void {
    if (this.previewAudio) {
      this.previewAudio.pause();
      this.previewAudio.currentTime = 0;
      this.previewAudio = null;
      this.settingsModal.setPreviewButtonState(false);
    }
  }

  private updateDocumentTitle(locale?: UILocale): void {
    const currentLocale = locale || this.i18n.getLocale();
    const title = this.i18n.t(I18N_KEYS.APP_TITLE);
    document.title = title;
    
    // Update HTML lang attribute
    const htmlElement = document.documentElement;
    htmlElement.lang = currentLocale;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      const description = currentLocale === 'vi' 
        ? 'Xướng Lô Tô - Vietnamese Loto Number Caller'
        : 'Loto Caller - Number Calling Game with Audio';
      metaDescription.setAttribute('content', description);
    }
  }
}
